import {
  CopyFilled,
  DeleteFilled,
  EditFilled,
  EyeFilled
} from "@ant-design/icons"
import { Button, Flex, Space, Tag, Tooltip, Typography } from "antd"
import React from "react"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import { TEMPLATE_DOCUMENT_TYPES_OBJ } from "../../constants/template-document-type.tsx"
import type { DocumentTemplate } from "../../types/types.ts"

const { Title, Text } = Typography

interface DocumentTemplatesListItemProps {
  template: DocumentTemplate
  //
  copyAllowed?: boolean
  deleteAllowed?: boolean
  editAllowed?: boolean
  //
  onDelete?: (id: string) => void
  onDuplicate?: (id: string) => void
  onPreview?: (id: string) => void
}

export function DocumentTemplatesListItem({
  template,
  copyAllowed,
  deleteAllowed,
  editAllowed,
  onDelete,
  onDuplicate,
  onPreview
}: DocumentTemplatesListItemProps) {
  const onDeleteHandler = async () => {
    onDelete?.(template?.id)
  }

  const onDuplicateHandler = async () => {
    onDuplicate?.(template?.id)
  }

  const docType =
    template?.doc_type && TEMPLATE_DOCUMENT_TYPES_OBJ?.[template?.doc_type]
  const docName =
    docType?.label || template?.doc_type?.toUpperCase() || "No type"

  return (
    <Flex
      justify="space-between"
      align="center"
      gap="middle"
      // No `last:border-b-0`: the pager sits in the same card body, so the last
      // row is never `:last-child`. Its own bottom border is the rule above the
      // footer, which is why the footer draws none of its own.
      className="px-4 py-3 border-b border-gray-200 hover:bg-gray-50"
    >
      {/* Template Info */}
      <Flex vertical>
        <Space className={"mb-1"}>
          <Title level={5} className={"mb-0"}>
            {template.name}
          </Title>
          {!template?.removable && (
            <Tooltip title="This template is provided by default and cannot be edited or deleted">
              <Tag className={"border-2"}>Default</Tag>
            </Tooltip>
          )}
        </Space>
        <Space size="middle">
          <Tag style={docType?.style}>{docName}</Tag>
          <Text type="secondary">
            {template?.blocks_count || template?.blocks?.length} blocks
          </Text>
          <Text type="secondary">{template.created_at}</Text>
        </Space>
      </Flex>

      {/* Action Buttons */}
      <Space size="small">
        {copyAllowed && (
          <Tooltip title="Duplicate">
            <Button
              icon={<CopyFilled />}
              type={"primary"}
              onClick={onDuplicateHandler}
            />
          </Tooltip>
        )}

        <Tooltip title="Preview">
          <Button
            type={"primary"}
            icon={<EyeFilled />}
            onClick={() => onPreview?.(template?.id)}
          />
        </Tooltip>

        {template?.removable && (
          <>
            {editAllowed && (
              <Tooltip title="Edit">
                <Link to={RoutesPaths.documentTemplates.edit(template?.id)}>
                  <Button icon={<EditFilled />} type={"primary"} />
                </Link>
              </Tooltip>
            )}

            {deleteAllowed && (
              <Tooltip title="Delete">
                <Button
                  icon={<DeleteFilled />}
                  variant={"solid"}
                  color={"red"}
                  onClick={onDeleteHandler}
                />
              </Tooltip>
            )}
          </>
        )}
      </Space>
    </Flex>
  )
}
